import { useLabelVersion } from '../../../../../features/labels/LabelsProvider';
import { labelId } from '../../../../../features/labels/labels';


import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { renderAggregatesCentral, transformCenterData } from '../Polar/utils/polarChartUtils';
import { isFedDay, sanitizeSfr, averageSfrFed } from '../../constants/constantsAnalytic';

// 1) Registramos los módulos necesarios para un Radar Chart
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title
);

const buildValuesSummary = (centerObj) => {
  if (Array.isArray(centerObj.values_summary)) {
    return centerObj.values_summary;
  }

  if (centerObj.analytic_data && typeof centerObj.analytic_data === 'object') {
    const firstItem = Object.values(centerObj.analytic_data)?.[0];
    const isFlat = firstItem?.values_summary || firstItem?.tasa_summary;

    if (isFlat) {
      return Object.values(centerObj.analytic_data).flatMap((cage) => {
        const vs = cage.values_summary;

        // Transformar el objeto values_summary en array de { day, value }
        if (vs && typeof vs === 'object' && !Array.isArray(vs)) {
          return Object.entries(vs).map(([day, value]) => ({ day, value }));
        }

        return Array.isArray(vs) ? vs : [];
      });
    } else {
      // Si hay una capa intermedia (ej. zona → jaula → values_summary)
      return Object.values(centerObj.analytic_data).flatMap((zona) =>
        Object.values(zona).flatMap((cage) => {
          const vs = cage.values_summary;
          if (vs && typeof vs === 'object' && !Array.isArray(vs)) {
            return Object.entries(vs).map(([day, value]) => ({ day, value }));
          }
          return Array.isArray(vs) ? vs : [];
        })
      );
    }
  }

  return [];
};


// 3) Función que, dado un arreglo de strings “DD/MM/YYYY”, devuelve ese arreglo
//    ordenado cronológicamente (día/mes/año).
const sortDaysChronologically = (daysArray) => {
  return daysArray
    .map((d) => {
      const [dd, mm, yyyy] = d.split('/').map((n) => parseInt(n, 10));
      return { original: d, dateObj: new Date(yyyy, mm - 1, dd) };
    })
    .sort((a, b) => a.dateObj - b.dateObj)
    .map((x) => x.original);
};


export default function RadarDayComparisonChart({
  versus = [],       // array de objetos: { name, values_summary? , analytic_data? }
  selected = [],     // array de strings con los nombres de los centros a mostrar
  selectedMetric,    // string con el nombre de la métrica que queremos graficar
  metrics,           // array de objetos: { key, label }
  colors             // array de cadenas CSS tipo "rgb(r, g, b)"
  ,showAllMetrics,
  availableMetrics,
  selectedMetricsKeys,
  nivelSeleccionado
}) {
  const labelsRevision = useLabelVersion();
  const chartRef = useRef();
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  // Tamaño de fuente dinámico para ejes
  const fontSizeEjes = Math.max(window.innerWidth * 0.005, 10);
  const tooltipFontSize = window.innerWidth * 0.007;

  // 4) Buscar el objeto { key, label } de la métrica seleccionada
  const metricObj = metrics.find((m) => m.key === selectedMetric);
  // Si no se encuentra, se queda con la misma key
  const metricLabel = metricObj ? metricObj.label : selectedMetric;


    // --------------------------------------------------
  // Filtrar “jaulas seleccionadas” y tomar hasta 4
  const displayedSeries = useMemo(() => {
    if (nivelSeleccionado === 'region') {
      // Aplanar todas las jaulas de cada centro seleccionado
      const processed = [];

      versus
        .filter(center => selected.includes(center.name))
        .forEach(center => {
          const cages = center.analytic_data || {};

          Object.entries(cages).forEach(([jaulaId, jaula]) => {
            const vs = jaula.values_summary;

            const values_summary = vs && typeof vs === 'object' && !Array.isArray(vs)
              ? Object.entries(vs).map(([day, value]) => ({ day, value }))
              : Array.isArray(vs) ? vs : [];

            processed.push({
              name: jaula.name || jaulaId,
              values_summary
            });
          });
        });

      return processed.slice(0, 4);
    }
    else if( nivelSeleccionado === 'area'){
      // Agrupar y acumular datos por centro
      const groupedData = transformCenterData(versus);
      // Filtrar solo los centros seleccionados y tomar hasta 4
      return groupedData.filter((center) => selected.includes(center.seriesName)).slice(0, 4);
    } else if( nivelSeleccionado === 'centro'){
      return versus
      .filter((serieObj) => selected.includes(serieObj.name))
      .slice(0, 4);
    }
    
  }, [nivelSeleccionado, versus, selected]);

  // --------------------------------------------------
  // 1) Calcular agregados por jaula (serie) para todas las métricas
  const aggregatedBySeries = useMemo(() => {
      return displayedSeries?.map((serieObj) => {
        const sums = {
          delivered_food: 0,
          alarms_count: 0,
          perdidos_count: 0,
          visitas_count: 0,
        };
        let sfrSum = 0;
        let sfrCount = 0;
  
        serieObj?.values_summary?.forEach((item) => {
          const vals = item.value;
          const df = Number(vals.delivered_food);
          if (!isNaN(df)) sums.delivered_food += df;
  
          const ac = Number(vals.alarms_count);
          if (!isNaN(ac)) sums.alarms_count += ac;
  
          // const pc = Number(vals.perdidos_count);
          const pc = Number(vals.perdidos_count);
          if (!isNaN(pc)) sums.perdidos_count += pc;
  
          // const vc = Number(vals.visitas_count);
          const vc = Number(vals.visitas_count);
          if (!isNaN(vc)) sums.visitas_count += vc;
  
          // SFR promedio: solo días alimentados (excluye ayunos) y con SFR saneado (>1000% recalculado/descartado)
          const sv = sanitizeSfr(vals);
          if (isFedDay(vals) && Number.isFinite(sv)) { // null (SFR irrecuperable) se ignora
            sfrSum += sv;
            sfrCount += 1;
          }
        });
        console.log('serieObj',serieObj)
        return {
          seriesName:    serieObj.name || serieObj.seriesName,
          delivered_food: sums.delivered_food,
          alarms_count:   sums.alarms_count,
          perdidos_count:  sums.perdidos_count,
          visitas_count:   sums.visitas_count,
          sfr_value:    sfrCount > 0 ? sfrSum / sfrCount : 0,
        };
      });
  }, [displayedSeries]);

  // Función auxiliar: convierte "rgb(r, g, b)" en un string "r, g, b"
  const extractRgbNumbers = (rgbString) => {
    // Ejemplo: rgbString = "rgb(99, 132, 255)"
    const insideParens = rgbString.replace(/^.*?\(/, '').replace(/\).*$/, '');
    // ahora "99, 132, 255"
    return insideParens;
  };

 useEffect(() => {
    const centers = versus
      .filter((c) => selected.includes(c.name))
      .map((c) => ({ name: c.name, values_summary: buildValuesSummary(c) }));

    if (!centers.length) {
      setChartData({ labels: [], datasets: [] });
      return;
    }

    if (showAllMetrics) {
      // Solo métricas disponibles (sin 'sfr')
      const filteredMetrics = availableMetrics.filter((m) =>
        selectedMetricsKeys.includes(m.key)
      );

      const labels = filteredMetrics.map((m) => m.label);
      const datasets = centers.map((center, idx) => {
        const total = center.values_summary.length;
        const data = filteredMetrics.map((m) => {
          if (total === 0) return '';
          let avg;
          if (m.key === 'sfr_value') {
            // SFR: promedio solo de días alimentados y saneado (>1000% recalculado/descartado)
            avg = Math.round(averageSfrFed(center.values_summary));
          } else {
            const sum = center.values_summary.reduce(
              (acc, { value }) => acc + (value[m.key] || 0),
              0
            );
            // delivered_food ya viene en [ton]; NO se divide de nuevo por 1000
            avg = Math.round(sum / total);
          }
          return avg === 0 ? '' : avg;
        });
        const baseRgb = extractRgbNumbers(colors[idx % colors.length]);
        return {
          label: labelId(center.name, nivelSeleccionado === "region" ? "ra" : "rac", center.name),
          entityKey: center.name,
          data,
          fill: true,
          backgroundColor: `rgba(${baseRgb}, 0.2)`,
          borderColor: `rgba(${baseRgb}, 1)`,
          pointBackgroundColor: `rgba(${baseRgb}, 1)`,
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: `rgba(${baseRgb}, 1)`,
        };
      });

      setChartData({ labels, datasets });
      return;
    }

    // MODO VER POR DÍAS
    const daysSet = new Set();
    centers.forEach((c) =>
      c.values_summary.forEach(({ day }) => daysSet.add(day))
    );
    if (!daysSet.size) {
      setChartData({ labels: [], datasets: [] });
      return;
    }

    const sortedDays = sortDaysChronologically(Array.from(daysSet));
    const datasets = centers.map((center, idx) => {
      const map = {};
      center.values_summary.forEach(({ day, value }) => {
        // SFR saneado (>1000% recalculado/descartado); resto sin conversión
        map[day] = selectedMetric === 'sfr_value' ? sanitizeSfr(value) : (value[selectedMetric] ?? 0);
      });
      const data = sortedDays.map((day) => {
        // delivered_food ya viene en [ton]; null (SFR irrecuperable) → 0 → hueco
        let v = map[day] ?? 0;
        return v === 0 ? '' : v;
      });
      const baseRgb = extractRgbNumbers(colors[idx % colors.length]);
      return {
        label: labelId(center.name, nivelSeleccionado === "region" ? "ra" : "rac", center.name),
          entityKey: center.name,
        data,
        fill: true,
        backgroundColor: `rgba(${baseRgb}, 0.2)`,
        borderColor: `rgba(${baseRgb}, 1)`,
        pointBackgroundColor: `rgba(${baseRgb}, 1)`,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: `rgba(${baseRgb}, 1)`,
      };
    });

    setChartData({ labels: sortedDays, datasets });
  }, [versus, selected, selectedMetric, metrics, colors, showAllMetrics, selectedMetricsKeys, nivelSeleccionado, labelsRevision]);
  // useEffect(() => {
  //   // 1) Filtrar solo los centros cuyo name esté en `selected`,
  //   //    y luego extraerles un objeto { name, values_summary } uniforme.
  //   const centersWithSummaries = versus
  //     .filter((center) => selected.includes(center.name))
  //     .map((center) => {
  //       const summaryArray = buildValuesSummary(center);
  //       return {
  //         name: center.name,
  //         values_summary: summaryArray,
  //       };
  //     });

  //   // Si no hay centros seleccionados, limpiamos y salimos
  //   if (!centersWithSummaries.length) {
  //     const emptyData = { labels: [], datasets: [] };
  //     if (JSON.stringify(emptyData) !== JSON.stringify(chartData)) {
  //       setChartData(emptyData);
  //     }
  //     return;
  //   }

  //   // === MODO “TODAS LAS MÉTRICAS” ===
  //   if (showAllMetrics) {
  //     // 1.1) Listado de labels en el radar: usar labels de cada métrica
  //     const metricLabels = metrics.map((m) => m.label);

  //     // 1.2) Para cada centro, calcular el promedio de cada métrica sobre todos los días
  //     const datasetsMetrics = centersWithSummaries.map((center, idx) => {
  //       const { values_summary } = center;
  //       const totalDias = values_summary.length;

  //       // Para cada métrica (key), calculamos promedio sobre todos los días
  //       const dataArray = metrics.map((m) => {
  //         const key = m.key;
  //         // Sumar valores del key en cada { day, value }
  //         const suma = values_summary.reduce((acc, { value }) => {
  //           // Si value[key] existe, lo sumamos; si no, sumamos 0
  //           return acc + (value[key] || 0);
  //         }, 0);

  //         if (totalDias === 0) return '';
  //         let promedio = Math.round(suma / totalDias);

  //         // ← CONVERSIÓN A TONELADAS: si la métrica es delivered_food, dividimos por 1000
  //         if (key === 'delivered_food') {
  //           promedio = Math.round((suma / totalDias) / 1000);
  //         }

  //         return promedio === 0 ? '' : promedio;
  //       });

  //       // -------------- Uso de `colors` en lugar de paleta interna --------------
  //       const baseRgb = extractRgbNumbers(colors[idx % colors.length]);

  //       return {
  //         label: center.name,
  //         data: dataArray,
  //         fill: true,
  //         backgroundColor: `rgba(${baseRgb}, 0.2)`,
  //         borderColor: `rgba(${baseRgb}, 1)`,
  //         pointBackgroundColor: `rgba(${baseRgb}, 1)`,
  //         pointBorderColor: '#fff',
  //         pointHoverBackgroundColor: '#fff',
  //         pointHoverBorderColor: `rgba(${baseRgb}, 1)`,
  //       };
  //     });

  //     // 1.3) Armar el objeto final para Radar Chart en modo “todas las métricas”
  //     const newChartData = {
  //       labels: metricLabels,
  //       datasets: datasetsMetrics,
  //     };
  //     // Actualizamos solo si cambió
  //     if (JSON.stringify(newChartData) !== JSON.stringify(chartData)) {
  //       setChartData(newChartData);
  //     }
  //     return; // Salimos aquí porque ya procesamos esta vista
  //   }

  //   // === MODO “VER POR DÍAS” (lógica original con conversión) ===
  //   // 2) Construir un Set con todos los días únicos de los centros seleccionados
  //   const allDaysSet = new Set();
  //   centersWithSummaries.forEach((center) => {
  //     center.values_summary.forEach(({ day }) => {
  //       allDaysSet.add(day);
  //     });
  //   });

  //   // Si no hay días, limpiamos y salimos
  //   if (!allDaysSet.size) {
  //     const emptyData = { labels: [], datasets: [] };
  //     if (JSON.stringify(emptyData) !== JSON.stringify(chartData)) {
  //       setChartData(emptyData);
  //     }
  //     return;
  //   }

  //   // 3) Ordenamos cronológicamente esos días
  //   const sortedDays = sortDaysChronologically(Array.from(allDaysSet));

  //   // 4) Para cada centro, construimos un mapa día→valor de la métrica,
  //   //    y luego generamos un array `data` alineado a sortedDays.
  //   const datasetsByDay = centersWithSummaries.map((center, idx) => {
  //     // Mapa día → valor de métrica
  //     const dayValueMap = {};
  //     center.values_summary.forEach(({ day, value }) => {
  //       dayValueMap[day] = value[selectedMetric] ?? 0;
  //     });

  //     // Construimos el arreglo de datos en el mismo orden que sortedDays,
  //     // reemplazando ceros por '' para que no se muestren en el radar,
  //     // y aplicando conversión si es delivered_food
  //     const dataArray = sortedDays.map((day) => {
  //       let rawValue = dayValueMap[day] ?? 0;

  //       // ← CONVERSIÓN A TONELADAS: si la métrica es delivered_food, dividimos por 1000
  //       if (selectedMetric === 'delivered_food') {
  //         rawValue = rawValue / 1000;
  //       }

  //       return rawValue === 0 ? '' : rawValue;
  //     });

  //     // -------------- Uso de `colors` en lugar de paleta interna --------------
  //     const baseRgb = extractRgbNumbers(colors[idx % colors.length]);

  //     return {
  //       label: center.name,
  //       data: dataArray,
  //       fill: true,
  //       backgroundColor: `rgba(${baseRgb}, 0.2)`,
  //       borderColor: `rgba(${baseRgb}, 1)`,
  //       pointBackgroundColor: `rgba(${baseRgb}, 1)`,
  //       pointBorderColor: '#fff',
  //       pointHoverBackgroundColor: '#fff',
  //       pointHoverBorderColor: `rgba(${baseRgb}, 1)`,
  //     };
  //   });

  //   // 5) Armar el objeto final para Radar Chart en modo “por días”
  //   const newChartData = {
  //     labels: sortedDays,
  //     datasets: datasetsByDay,
  //   };

  //   // 6) Solo actualizamos si cambió algo
  //   if (JSON.stringify(newChartData) !== JSON.stringify(chartData)) {
  //     setChartData(newChartData);
  //   }
  // }, [versus, selected, selectedMetric, metrics, showAllMetrics, chartData, colors]);

  // 7) Opciones específicas para que se vea legible:
  
  const options = {
    responsive: true,
    // 1) Cambiamos el modo de interacción a “nearest” para detectar cualquier slice
    interaction: {
      mode: 'index',        // igual que en tu tooltip
      intersect: true      // para que al pasar cerca del punto se active
    },
    // 3) Definimos el tamaño base y hover de cada punto
    elements: {
      point: {
        // si el punto está “activo” (hover), radio=10; si no, 4
        radius: context => (context.active ? 10 : 4),
        hoverRadius: 10
      }
    },
    scales: {
      r: {
        grid: {
          color: 'rgba(255, 255, 255, 0.29)',
        },
        angleLines: {
          color: 'rgba(255,255,255, 0.29)',
        },
        pointLabels: {
          display: true,
          centerPointLabels: true,
          font: { size: fontSizeEjes },
          color: '#c3c3c3',
        },
        ticks: {
          beginAtZero: true,
          min: 0,
          precision: 0,
          backdropColor: 'transparent',
          color: '#c3c3c3',
          padding: 6,
          callback: (value) => (value === 0 ? '' : value),
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: Math.max(fontSizeEjes, 12) },
          color: '#c3c3c3',
        },
      },
      title: {
        display: true,
        text: showAllMetrics
          ? 'Promedio de todas las métricas'
          : `Evolución diaria de ${metricLabel}`,
        color: '#c3c3c3',
        font: { size: Math.max(fontSizeEjes * 1.2, 14) },
      },
      // tooltip: {
      //   titleColor: '#c3c3c3',
      //   bodyColor: '#c3c3c3',
      //   borderColor: '#a3a3a3',
      //   borderWidth: 1,
      // },
      tooltip: {
        // usamos callbacks para formatear la etiqueta
        // callbacks: {
        //   title: items => items[0]?.label ?? '',
        //   label: ctx => {
        //     const v = ctx.raw;
        //     return `${ctx.dataset.label}: ${typeof v === 'number' ? v.toFixed(2) : v}`;
        //   }
        // },
        borderColor: '#a3a3a3',
        borderWidth: 1,
        titleColor: '#c3c3c3',
        bodyColor: '#c3c3c3',
        bodyFont: {
          size: tooltipFontSize,  // ← aquí usas vw convertido a px
        },
        titleFont: {
          size: tooltipFontSize * 1.1, // un poco más grande para el título
        },
      }
    },
  };

  const renderAggregatesCentralProps = {colors,aggregatedBySeries}

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignContent:'center', alignItems:'center', height: '60vh', flexDirection:'column' }}>
      {/* Contenedor del radar */}
      <div
        style={{
          width: '33vw',
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          flexDirection: 'row',
          // marginTop: "5vh",
          height:'14vh',
          // border:'solid 0.01vw'
        }}
      >
        {renderAggregatesCentral(renderAggregatesCentralProps)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignContent:'center', alignItems:'center', height: '46vh', width:'100%', flexDirection:'column', }}>
        <Radar datasetIdKey="entityKey" ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
}
